import type { BlocoHorario, Espelho } from "@/api/escala.service";
import { BLOCOS, blocoDoDia, type NomeBloco } from "@/components/escala/cargaHoraria";

// Colunas dos quadros: o bloco da carga que vale em cada tipo de dia.
export const ROTULO_TIPO_DE_DIA: Record<NomeBloco, string> = {
  util: "Segunda a sexta",
  sabado: "Sábado",
  domingo: "Domingo",
  feriado: "Feriado",
};

// "07" na hora cheia, "13:40" fora dela.
export const horaCurta = (hora: string) => (hora.endsWith(":00") ? hora.slice(0, 2) : hora);

// "07 às 12 e 13:40 às 16"; sem intervalo, "08 às 13:40".
export function formatarHorarioCurto(b: BlocoHorario) {
  return b.intervalo_inicio && b.intervalo_fim
    ? `${horaCurta(b.entrada)} às ${horaCurta(b.intervalo_inicio)} e ${horaCurta(b.intervalo_fim)} às ${horaCurta(b.saida)}`
    : `${horaCurta(b.entrada)} às ${horaCurta(b.saida)}`;
}

export interface HorarioDoQuadro {
  texto: string;
  // Cor da carga, como foi cadastrada; sem ela é "Padrão".
  cor?: string;
}

export type QuadroDeHorarios = Record<NomeBloco, HorarioDoQuadro[]>;

// Horários aplicados na grade, com as trocas manuais, sem repetir, por tipo de dia e pela entrada.
// É a legenda das cores da grade: o mesmo horário em duas cores sai uma vez por cor.
export function quadroDeHorarios(espelho: Espelho): QuadroDeHorarios {
  const cores = new Map((espelho.cargas ?? []).map((c) => [c.id, c.cor]));
  const porBloco = new Map<NomeBloco, Map<string, HorarioDoQuadro & { ordem: string }>>(BLOCOS.map((b) => [b, new Map()]));

  for (const d of espelho.dias) {
    if (d.tipo !== "T" || !d.horario?.entrada) continue;
    const cor = d.carga_horaria_id ? cores.get(d.carga_horaria_id) : undefined;
    const texto = formatarHorarioCurto(d.horario);
    const { entrada, intervalo_inicio = "", intervalo_fim = "", saida } = d.horario;
    porBloco.get(blocoDoDia(d.data, espelho.feriados))!.set(`${texto}|${cor ?? ""}`, {
      texto,
      cor,
      // "HH:MM" em ordem de texto é a ordem do relógio.
      ordem: [entrada, intervalo_inicio, intervalo_fim, saida, cor ?? ""].join("|"),
    });
  }

  return Object.fromEntries(
    BLOCOS.map((b) => [
      b,
      [...porBloco.get(b)!.values()].sort((x, y) => x.ordem.localeCompare(y.ordem)).map(({ texto, cor }) => ({ texto, cor })),
    ]),
  ) as QuadroDeHorarios;
}

export interface EntradasDoTipo {
  // Hora cheia ("07") e quantos entram nela, só as horas com alguém.
  linhas: { hora: string; qtde: number }[];
  total: number;
}

export type QuadroDeEntradas = Record<NomeBloco, EntradasDoTipo>;

// Quantos colaboradores entram em cada hora cheia, por tipo de dia, pela carga padrão da própria
// gerada: a entrada dos dias T não trocados à mão, não o cadastro de hoje. Se o colaborador teve
// mais de uma, vale a mais frequente (no empate, a mais cedo). Quem não teve dia T não manual num
// tipo de dia não entra nele.
export function quadroDeEntradas(espelho: Espelho): QuadroDeEntradas {
  // colaborador|bloco -> entrada -> dias
  const contagem = new Map<string, Map<string, number>>();
  for (const d of espelho.dias) {
    if (d.tipo !== "T" || d.manual || !d.horario?.entrada) continue;
    const chave = `${d.colaborador_id}|${blocoDoDia(d.data, espelho.feriados)}`;
    const entradas = contagem.get(chave) ?? new Map<string, number>();
    entradas.set(d.horario.entrada, (entradas.get(d.horario.entrada) ?? 0) + 1);
    contagem.set(chave, entradas);
  }

  const porHora = new Map<NomeBloco, Map<string, number>>(BLOCOS.map((b) => [b, new Map()]));
  for (const [chave, entradas] of contagem) {
    const bloco = chave.slice(chave.indexOf("|") + 1) as NomeBloco;
    const [entrada] = [...entradas].sort(([ea, na], [eb, nb]) => nb - na || ea.localeCompare(eb))[0]!;
    const hora = entrada.slice(0, 2);
    const horas = porHora.get(bloco)!;
    horas.set(hora, (horas.get(hora) ?? 0) + 1);
  }

  return Object.fromEntries(
    BLOCOS.map((b) => {
      const linhas = [...porHora.get(b)!].sort(([a], [z]) => a.localeCompare(z)).map(([hora, qtde]) => ({ hora, qtde }));
      return [b, { linhas, total: linhas.reduce((soma, l) => soma + l.qtde, 0) }];
    }),
  ) as QuadroDeEntradas;
}
